import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade6Zhuyin.css";

function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade6Zhuyin
 *  - Reading longer texts, mini-essays, deeper vocabulary usage
 *  - Single quiz pool => 25 Q
 */

const advancedReadingNotes = (
  <div className="g6z-reading-notes">
    <h4>Longer Texts & Deeper Vocabulary in Zhuyin</h4>
    <p>
      Grade 6 focuses on reading mini-essays or articles in full Zhuyin.  
      You’ll encounter more abstract vocabulary, idioms, and deeper grammar.
    </p>
    <ul>
      <li>Stories may be 3–5 paragraphs long, discussing events or personal reflections.</li>
      <li>Look out for idiomatic expressions: ㄕㄡˇ ㄍㄨㄥ ㄓㄨㄥ ㄒㄧㄢ (手工中心?), etc.</li>
      <li>Continue bridging to full character recognition while using Zhuyin as a phonetic guide.</li>
    </ul>
  </div>
);

const zhuyinQuestions = [
  {
    question: "At Grade 6, reading mini-essays in Zhuyin helps you with...?",
    correctAnswer: "Transitioning to advanced text & building deeper vocab",
    options: [
      "Forgetting characters completely",
      "Only reading short lines always",
      "Transitioning to advanced text & deeper vocab"
    ]
  },
  {
    question: "An abstract phrase: ㄓ ㄏㄨㄥˊ ㄉㄜ˙ ㄗㄨㄟˇ => 'red mouth'? Could be idiomatic or metaphorical. We rely on context to interpret. True?",
    correctAnswer: "True, context often reveals idiomatic meanings in essays",
    options: [
      "False, we read it literally only",
      "Zhuyin can’t show idioms",
      "True, context clarifies idioms or metaphors"
    ]
  },
  {
    question: "If a text references ㄅㄨˋ ㄍㄢˇ ㄉㄤ => 'bù gǎn dāng,' a polite phrase meaning 'I don’t deserve [the praise].' This shows?",
    correctAnswer: "Zhuyin can represent polite idioms; context reveals the nuance",
    options: [
      "We skip idioms in Zhuyin",
      "No nuance needed",
      "Context & knowledge reveal polite meaning"
    ]
  },
  {
    question: "Mini-essays might include ㄒㄧㄤˇ ㄈㄠˇ ㄏㄨㄟˋ => 'to reflect on a matter'? Tones are crucial. If incorrect, it might become...?",
    correctAnswer: "A different phrase or nonsense, so correct tone is vital",
    options: [
      "No difference, all the same",
      "It’s still read the same way",
      "A different phrase or nonsense"
    ]
  },
  {
    question: "Longer paragraphs may contain transition words like ㄅㄨˊ ㄐㄧㄣˇ... ㄦˊ ㄑㄧㄝˇ => 'Not only... but also...' This helps show?",
    correctAnswer: "Complex linking structures in Mandarin grammar",
    options: [
      "We only use short connectors",
      "No advanced grammar in essays",
      "Complex linking grammar usage"
    ]
  },
  {
    question: "If you see an idiom: ㄏㄨˊ ㄌㄧˊ ㄏㄨㄗ ㄍㄣ => 'fox and tiger synergy'? Possibly means a fable or metaphor. Should you...?",
    correctAnswer: "Check references or footnotes to interpret the idiom context",
    options: [
      "Ignore it because it’s not real",
      "Force a literal meaning only",
      "Check references or footnotes"
    ]
  },
  {
    question: "Reading an essay about travel might mention ㄏㄠˇ ㄐㄧ ㄍㄜ˙ ㄔㄥ ㄕˋ => 'several cities.' The measure word ㄍㄜ˙ is used for?",
    correctAnswer: "Generic measure word for 'cities', 'places', etc.",
    options: [
      "Animals only",
      "No measure words needed for cities",
      "A generic measure word for many nouns"
    ]
  },
  {
    question: "Some advanced vocab might be ㄓㄨㄢˇ ㄧㄢˇ => 'in the blink of an eye.' This is a figurative phrase meaning?",
    correctAnswer: "Time passes quickly, 'in an instant.'",
    options: [
      "A literal blinking competition",
      "No meaning in real usage",
      "Time passes quickly or instantly"
    ]
  },
  {
    question: "If the text says ㄊㄠˊ ㄒㄧㄥˋ ㄏㄨˊ ㄌㄧ => 'peach blossom fox'? Could be an allusion to a story or expression. Should you?",
    correctAnswer: "Consult context or references to see if it’s an idiom or literal phrase",
    options: [
      "Assume it’s random nonsense",
      "Take it 100% literal always",
      "Check references for possible idiom or story"
    ]
  },
  {
    question: "Deeper grammar like ㄍㄨㄤ ㄕˋ => 'guāng shì' might mean 'only, merely.' If you see ㄨㄛˇ ㄍㄨㄤ ㄕˋ ㄎㄢˋ ㄅㄧˇ => 'I’m just looking at pens,' context implies?",
    correctAnswer: "I’m not buying them, just looking, clarifying a polite refusal maybe",
    options: [
      "I must buy them all",
      "No subtlety implied",
      "It’s a polite way to say 'just browsing'"
    ]
  },
  {
    question: "Mini-essays might end with ㄒㄧ ㄔㄥˊ => 'to conclude' or 'in conclusion.' This helps structure writing. True?",
    correctAnswer: "Yes, signifying a final summary or conclusion in the text",
    options: [
      "No, we never use concluding words",
      "Yes, it’s common to show ending or summary",
      "Zhuyin can’t represent final summaries"
    ]
  },
  {
    question: "In advanced usage, ㄕㄨㄛ ㄈㄨˊ => 'shuōfú' (to persuade), might appear. If tone is incorrect, e.g. ㄕㄨㄛˋ ㄈㄨˊ => could be nonsense. So?",
    correctAnswer: "Tone accuracy is again crucial for advanced verbs",
    options: [
      "We ignore tone at Grade 6",
      "All tone combos are valid words",
      "We must keep correct tone to convey meaning"
    ]
  },
  {
    question: "Reading an essay about childhood might mention ㄏㄨㄛˊ ㄆㄛ (lively) or ㄏㄨㄛˊ ㄌㄧˋ (vitality). Subtle tone differences => different words. True or false?",
    correctAnswer: "True, small tone changes can shift meaning drastically",
    options: [
      "False, they’re all the same meaning",
      "True, small tone changes can shift meaning",
      "No difference at all"
    ]
  },
  {
    question: "A phrase like ㄨㄛˇ ㄒㄧㄤˇ ㄉㄜ˙ ㄏㄣˇ ㄕㄣ => 'I think deeply about it' might appear in reflective essays. Why is this relevant for Grade 6?",
    correctAnswer: "Essays may delve into personal reflections or deeper thoughts in Zhuyin",
    options: [
      "We only read factual statements",
      "No reflections in 6th grade",
      "Yes, deeper personal essays appear at this level"
    ]
  },
  {
    question: "If you see ㄐㄧㄝˋ ㄔㄨ ㄉㄠˋ => 'borrow something to say something'? Possibly a rhetorical device meaning 'using an example as a metaphor.' This indicates?",
    correctAnswer: "Higher-level rhetorical usage in Mandarin, represented via Zhuyin as well",
    options: [
      "We can’t do metaphors in Zhuyin",
      "Only pinyin can handle rhetorical usage",
      "Zhuyin also conveys advanced rhetorical devices"
    ]
  },
  {
    question: "An essay might use ㄈㄨˋ ㄗㄨ ㄐㄧㄢˇ => 'to review and simplify'? This shows you can...?",
    correctAnswer: "Handle advanced verbs or expressions in full Zhuyin text",
    options: [
      "Need to revert to pinyin",
      "Only read basic words",
      "Read advanced expressions in Zhuyin"
    ]
  },
  {
    question: "Idioms like ㄕˊ ㄐㄧ ㄗㄨㄛˋ ㄈㄨ => 'make do with present time'? Could appear. Should you skip them?",
    correctAnswer: "No, attempt to interpret or look them up; they enrich the reading experience",
    options: [
      "Yes, skip idioms always",
      "No, interpreting them adds cultural depth",
      "We only read half of them"
    ]
  },
  {
    question: "When you see 3–5 paragraphs in Zhuyin, a good strategy is?",
    correctAnswer: "Read paragraph by paragraph, summarizing main points, clarifying new words",
    options: [
      "Read only the first line",
      "Scan for known words, ignore unknown ones",
      "Read carefully, summarize, clarify unknown vocab"
    ]
  },
  {
    question: "Some advanced grammar: ㄅㄧㄣ ㄓㄜˇ ㄅㄧㄣ ㄕˇ => 'both... and...' linking two aspects. This is typical in longer Mandarin texts. True?",
    correctAnswer: "Yes, complex linking for more nuanced statements",
    options: [
      "No, we only use short statements",
      "Yes, advanced linking structures appear in essays",
      "We skip linking words"
    ]
  },
  {
    question: "An essay might describe emotions: ㄐㄧ ㄉㄨˋ (jealousy), ㄎㄨㄥˇ ㄐㄩˋ (fear). Understanding these helps you...?",
    correctAnswer: "Expand emotional vocabulary & interpret deeper themes",
    options: [
      "Ignore emotional content",
      "Focus only on measure words",
      "Expand emotive vocab & interpret themes"
    ]
  },
  {
    question: "Is it normal to see partial usage of characters plus Zhuyin in advanced texts? E.g., '台灣 ㄑㄧㄥˊ'? Yes or no?",
    correctAnswer: "Yes, sometimes a mix is used for clarity or style, bridging to full characters",
    options: [
      "No, it’s forbidden",
      "Yes, a partial mix is common in transitional texts",
      "We only see 100% characters or 100% Zhuyin"
    ]
  },
  {
    question: "Finally, daily practice with longer Zhuyin texts fosters confidence for junior high reading. True or false?",
    correctAnswer: "True, consistent reading builds strong foundation for next levels",
    options: [
      "False, it’s optional and no help",
      "True, daily practice fosters a strong foundation",
      "No correlation"
    ]
  }
];

export default function Grade6Zhuyin() {
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 mins

  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Quick quiz
  const generateQuiz = () => {
    const q = zhuyinQuestions[Math.floor(Math.random() * zhuyinQuestions.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options])
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const handleAnswerClick = (ans) => {
    setSelectedAnswer(ans);
    if (quiz && ans === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Final quiz
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g6chi-page">
      <h1 className="g6chi-title">Grade 6 Zhuyin: Mini-Essays & Deeper Vocab</h1>
      <p className="g6chi-description">
        Read longer paragraphs or mini-essays fully in Zhuyin, exploring advanced vocabulary and grammar.
      </p>

      <div className="g6chi-lesson-content">
        <h2 className="g6chi-lesson-title">注音: Longer Texts & Abstract Usage</h2>
        <p className="g6chi-lesson-description">
          By Grade 6, you’ll encounter more complex articles or mini-essays in Zhuyin, including idioms and advanced grammar.
        </p>
        <p className="g6chi-lesson-example">
          <strong>Example:</strong> ㄅㄨˋ ㄍㄢˇ ㄉㄤ = “I don’t deserve (the praise).”
        </p>
        {advancedReadingNotes}
      </div>

      <div className="g6chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g6chi-quick-quiz-container">
          <h2 className="g6chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g6chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g6chi-quiz-question">{quiz.question}</p>
              <div className="g6chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g6chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g6chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g6chi-final-quiz-container">
          <h2 className="g6chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g6chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g6chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>Question {finalQuizIndex + 1} of {finalQuizQuestions.length}</p>
              <p className="g6chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g6chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g6chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
